import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { forkJoin, from, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  finalize,
  map,
  switchMap,
} from 'rxjs/operators';

import { SearchFormComponent } from '@shared/components/search-form/search-form.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import type { SearchFormData } from '@shared/components/search-form/search-form.component';
import type { SortEvent } from '@shared/components/data-table/data-table.component';
import {
  getSearchFormConfig,
  USER_ROLES_ASSIGNMENT_DEFAULT_COLUMNS,
  USER_ROLES_ASSIGNMENT_FLEXIBLE_COLUMNS,
} from './employee-mgmt.config';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { UsersService } from '@app/api/user-service/api/users.service';
import { EmployeeMgmtService } from './services/employee-mgmt.service';
import { KeycloakUserSearchRequest } from '@app/api/user-service/model/keycloakUserSearchRequest';
import { KeycloakUserResponse } from '@app/api/user-service/model/keycloakUserResponse';
import { KeycloakBulkUserRolesRequest } from '@app/api/user-service/model/keycloakBulkUserRolesRequest';
import { PermissionService } from '@app/core/services/permission.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { AssignRolesDialogComponent } from './components/assign-roles-dialog/assign-roles-dialog.component';
import type { AssignRolesDialogResult } from './components/assign-roles-dialog/assign-roles-dialog.component';
import { UnassignRolesDialogComponent } from './components/unassign-roles-dialog/unassign-roles-dialog.component';
import type { UnassignRolesDialogResult } from './components/unassign-roles-dialog/unassign-roles-dialog.component';

@Component({
  selector: 'app-employee-mgmt',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ToastModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    AppButtonComponent,
  ],
  providers: [DialogService, MessageService],
  templateUrl: './employee-mgmt.component.html',
  styleUrl: './employee-mgmt.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmployeeMgmtComponent {
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  readonly users = signal<KeycloakUserResponse[]>([]);
  readonly loading = signal(false);
  readonly totalRecords = signal(0);
  readonly selectedUsers = signal<
    (KeycloakUserResponse & {
      businessUnitDisplay: string;
      rolesDisplay: string;
    })[]
  >([]);
  readonly roleOptions = signal<{ label: string; value: string }[]>([]);
  readonly businessUnitOptions = signal<{ label: string; value: number }[]>([]);
  readonly sortField = signal<string>('username');
  readonly sortOrder = signal<1 | -1>(1);

  readonly searchParams = signal({
    firstname: '',
    lastname: '',
    username: '',
    email: '',
    role: '' as string,
    businessUnitId: null as number | null,
    page: 0,
    size: 20,
    sortBy: 'username',
    ascending: true,
  });

  private readonly destroyRef = inject(DestroyRef);
  private readonly usersService = inject(UsersService);
  private readonly userRolesAssignmentService = inject(
    EmployeeMgmtService,
  );
  private readonly translateService = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private readonly dialogService = inject(DialogService);
  private readonly messageService = inject(MessageService);
  private readonly searchTrigger$ = new Subject<SearchFormData>();
  private assignDialogRef: DynamicDialogRef | null = null;
  private unassignDialogRef: DynamicDialogRef | null = null;

  readonly canView = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.user.employee_management.view');
  });
  readonly canEdit = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.user.employee_management.edit');
  });

  readonly searchFormConfig = computed(() =>
    getSearchFormConfig({
      roleOptions: this.roleOptions(),
      businessUnitOptions: this.businessUnitOptions(),
    }),
  );

  readonly defaultColumns = computed(
    () => USER_ROLES_ASSIGNMENT_DEFAULT_COLUMNS,
  );
  readonly flexibleColumns = computed(
    () => USER_ROLES_ASSIGNMENT_FLEXIBLE_COLUMNS,
  );

  constructor() {
    this.loadRolesAndBusinessUnits();
    this.setupSearchPipeline();
    this.searchTrigger$.next({});
  }

  private loadRolesAndBusinessUnits(): void {
    forkJoin({
      roles: this.userRolesAssignmentService.getRoleOptions(),
      businessUnits:
        this.userRolesAssignmentService.getBusinessUnitSearchOptions(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ roles, businessUnits }) => {
        this.roleOptions.set(roles);
        this.businessUnitOptions.set(businessUnits);
      });
  }

  private setupSearchPipeline(): void {
    this.searchTrigger$
      .pipe(
        debounceTime(100),
        switchMap(() => {
          this.loading.set(true);
          const params = this.searchParams();
          const attributes =
            params.businessUnitId != null
              ? ({
                  business_unit_ids: String(params.businessUnitId),
                } as unknown as { [key: string]: string })
              : undefined;
          const request: KeycloakUserSearchRequest = {
            page: params.page,
            size: params.size,
            sortBy: params.sortBy,
            ascending: params.ascending,
            firstName: params.firstname || undefined,
            lastName: params.lastname || undefined,
            username: params.username || undefined,
            email: params.email || undefined,
            roles:
              params.role != null && params.role !== ''
                ? [String(params.role)]
                : undefined,
            attributes,
          };
          return this.usersService.searchUsers(request, 'body', false).pipe(
            switchMap((body: unknown) => {
              if (body instanceof Blob) {
                const b = body as Blob;
                if (b.type?.includes('json')) {
                  return from(b.text()).pipe(
                    map((text) => JSON.parse(text) as Record<string, unknown>),
                  );
                }
              }
              return of(body as Record<string, unknown>);
            }),
            catchError(() => of({ totalElements: 0, pageContent: [] })),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((page) => {
        const rawContent =
          page.pageContent ?? (page as Record<string, unknown>)['pagecontent'];

        const content: (KeycloakUserResponse & Record<string, unknown>)[] =
          Array.isArray(rawContent) ? rawContent : [];

        const buIdToName = this.buildBuIdToNameMap();
        const rows = content.map(
          (u: KeycloakUserResponse & Record<string, unknown>) =>
            this.toTableRow(u, buIdToName),
        );

        this.users.set(rows);
        const total =
          page.totalElements ??
          (page as Record<string, unknown>)['totalelements'];
        const totalNum =
          typeof total === 'number' ? total : Math.max(0, rows.length);
        this.totalRecords.set(totalNum);
        this.loading.set(false);
      });
  }

  private buildBuIdToNameMap(): Map<string, string> {
    const map = new Map<string, string>();
    for (const opt of this.businessUnitOptions()) {
      map.set(String(opt.value), opt.label);
    }
    return map;
  }

  private toTableRow(
    u: KeycloakUserResponse & Record<string, unknown>,
    buIdToName: Map<string, string>,
  ): KeycloakUserResponse & {
    businessUnitDisplay: string;
    rolesDisplay: string;
  } {
    const rawRoles = u.realmRoles ?? u['realmroles'];
    let roles: string[];
    if (rawRoles == null) {
      roles = [];
    } else if (Array.isArray(rawRoles)) {
      roles = rawRoles as string[];
    } else {
      roles = Array.from((rawRoles as Set<string>) ?? []);
    }

    const attrs = (u.attributes ?? u['attributes']) as
      | Record<string, string[]>
      | undefined;
    const rawBuIds = attrs?.['business_unit_ids'];
    const buIds = Array.isArray(rawBuIds) ? rawBuIds : [];
    const buNames = buIds
      .map((id) => buIdToName.get(String(id)))
      .filter((name): name is string => name != null);
    const businessUnitDisplay = buNames.length > 0 ? buNames.join(', ') : '—';

    return {
      ...u,
      businessUnitDisplay,
      rolesDisplay: roles.length > 0 ? roles.join(', ') : '—',
    };
  }

  onSearch(formData: SearchFormData): void {
    this.selectedUsers.set([]);
    this.searchParams.update((p) => ({
      ...p,
      page: 0,
      firstname: (formData['firstname'] ?? '').toString().trim(),
      lastname: (formData['lastname'] ?? '').toString().trim(),
      username: (formData['username'] ?? '').toString().trim(),
      email: (formData['email'] ?? '').toString().trim(),
      role: (formData['role'] ?? '').toString().trim(),
      businessUnitId:
        formData['businessUnitId'] != null && formData['businessUnitId'] !== ''
          ? (formData['businessUnitId'] as number)
          : null,
    }));
    this.searchTrigger$.next({});
  }

  onReset(): void {
    this.selectedUsers.set([]);
    this.searchParams.set({
      firstname: '',
      lastname: '',
      username: '',
      email: '',
      role: '',
      businessUnitId: null,
      page: 0,
      size: 20,
      sortBy: 'username',
      ascending: true,
    });
    this.sortField.set('username');
    this.sortOrder.set(1);
    this.searchTrigger$.next({});
  }

  onPageChange(event: { first?: number; rows?: number }): void {
    const rows = event.rows ?? this.searchParams().size;
    const page =
      typeof event.first === 'number' ? Math.floor(event.first / rows) : 0;
    this.searchParams.update((p) => ({
      ...p,
      page,
      size: rows,
    }));
    this.searchTrigger$.next({});
  }

  onSortChange(sortEvent: SortEvent): void {
    this.sortField.set(sortEvent.field);
    this.sortOrder.set(sortEvent.order);
    this.searchParams.update((p) => ({
      ...p,
      sortBy: sortEvent.field,
      ascending: sortEvent.order === 1,
      page: 0,
    }));
    this.searchTrigger$.next({});
  }

  onSelectionChange(
    selection: (KeycloakUserResponse & {
      businessUnitDisplay: string;
      rolesDisplay: string;
    })[],
  ): void {
    // Merge: keep off-page selections, replace current-page selections with what the table reports
    const currentPageIds = new Set(
      this.users()
        .map((u) => u.id)
        .filter(Boolean),
    );
    const offPageSelections = this.selectedUsers().filter(
      (u) => !currentPageIds.has(u.id),
    );
    const onPageSelections = selection.filter((u) => currentPageIds.has(u.id));
    this.selectedUsers.set([...offPageSelections, ...onPageSelections]);
  }

  onAssignRoles(): void {
    if (this.selectedUsers().length === 0) return;
    const users = this.selectedUsers();
    const userIds = users.map((u) => u.id).filter((id): id is string => !!id);
    if (userIds.length === 0) return;

    this.assignDialogRef = this.dialogService.open(AssignRolesDialogComponent, {
      header: this.translateService.instant('configuration.user_roles_assignment.actions.assign_roles'),
      width: '960px',
      modal: true,
      closable: true,
      dismissableMask: true,
      data: { selectedUserIds: userIds },
    });

    this.assignDialogRef.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: AssignRolesDialogResult | undefined) => {
        this.assignDialogRef = null;
        if (!result) return;
        if (result.roleNames?.length) {
          this.executeAssignRolesBulk(userIds, result.roleNames);
        }
        if (result.businessUnitIds?.length) {
          this.executeAssignBusinessUnitsBulk(userIds, result.businessUnitIds);
        }
      });
  }

  onUnassignRoles(): void {
    if (this.selectedUsers().length === 0) return;
    const users = this.selectedUsers();
    const userIds = users.map((u) => u.id).filter((id): id is string => !!id);
    if (userIds.length === 0) return;
    const assignedRoleNames = this.getAssignedRoleNamesUnion(users);

    const userDisplays = users.map((u) => ({
      id: u.id ?? '',
      display: u.username ?? u.email ?? u.id ?? '',
    }));
    const assignedRolesWithUsers = this.getAssignedRolesWithUsers(users);
    this.unassignDialogRef = this.dialogService.open(
      UnassignRolesDialogComponent,
      {
        header: this.translateService.instant(
          'configuration.user_roles_assignment.actions.unassign_roles',
        ),
        width: '800px',
        modal: true,
        closable: true,
        dismissableMask: true,
        data: {
          selectedUserIds: userIds,
          assignedRoleNames,
          assignedRolesWithUsers,
          userDisplays,
        },
      },
    );

    this.unassignDialogRef.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: UnassignRolesDialogResult | undefined) => {
        this.unassignDialogRef = null;
        if (!result) return;
        if (result.roleNames?.length) {
          this.executeUnassignRolesBulk(userIds, result.roleNames);
        }
        if (result.businessUnits?.length) {
          this.executeUnassignBusinessUnitsBulk(result.businessUnits);
        }
      });
  }

  /** Union of all realm role names assigned to the given users. */
  private getAssignedRoleNamesUnion(
    users: (KeycloakUserResponse & {
      businessUnitDisplay: string;
      rolesDisplay: string;
    })[],
  ): string[] {
    const names = new Set<string>();
    for (const u of users) {
      const raw =
        u.realmRoles ?? (u as unknown as Record<string, unknown>)['realmroles'];
      if (Array.isArray(raw)) {
        raw.forEach((r) => names.add(String(r)));
      } else if (raw != null && typeof raw === 'object' && 'forEach' in raw) {
        (raw as Set<string>).forEach((r) => names.add(String(r)));
      }
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }

  /** For each role name, list display names of selected users who have that role. */
  private getAssignedRolesWithUsers(
    users: (KeycloakUserResponse & {
      businessUnitDisplay: string;
      rolesDisplay: string;
    })[],
  ): { roleName: string; usersDisplay: string }[] {
    const roleToDisplays = new Map<string, string[]>();
    for (const u of users) {
      const display = u.username ?? u.email ?? u.id ?? '';
      const raw =
        u.realmRoles ?? (u as unknown as Record<string, unknown>)['realmroles'];
      const roleNames: string[] = Array.isArray(raw)
        ? raw.map((r) => String(r))
        : raw != null && typeof raw === 'object' && 'forEach' in raw
          ? Array.from(raw as Set<string>)
          : [];
      for (const r of roleNames) {
        const list = roleToDisplays.get(r) ?? [];
        list.push(display);
        roleToDisplays.set(r, list);
      }
    }
    return Array.from(roleToDisplays.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([roleName, list]) => ({
        roleName,
        usersDisplay: [...new Set(list)].join(', '),
      }));
  }

  private executeAssignRolesBulk(userIds: string[], roleNames: string[]): void {
    this.loading.set(true);
    const bulkRequest = {
      userIds,
      roles: roleNames,
    } as unknown as KeycloakBulkUserRolesRequest;
    this.usersService
      .assignRolesBulk(bulkRequest, 'body', false)
      .pipe(
        switchMap((body: unknown) => {
          if (body instanceof Blob && body.type?.includes('json')) {
            return from(body.text()).pipe(map(() => undefined));
          }
          return of(undefined);
        }),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'configuration.user_roles_assignment.messages.assign_success',
            ),
          });
          this.selectedUsers.set([]);
          this.searchTrigger$.next({});
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'configuration.user_roles_assignment.messages.assign_failed',
            ),
          });
        },
      });
  }

  private executeAssignBusinessUnitsBulk(
    userIds: string[],
    businessUnitIds: number[],
  ): void {
    this.loading.set(true);
    const calls = businessUnitIds.map((businessUnitId) =>
      this.userRolesAssignmentService.assignUsersToBusinessUnitBulk({
        userIds: new Set(userIds),
        businessUnitId,
      }),
    );
    forkJoin(calls)
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'configuration.user_roles_assignment.messages.assign_bu_success',
            ),
          });
          this.selectedUsers.set([]);
          this.searchTrigger$.next({});
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'configuration.user_roles_assignment.messages.assign_bu_failed',
            ),
          });
        },
      });
  }

  private executeUnassignRolesBulk(
    userIds: string[],
    roleNames: string[],
  ): void {
    this.loading.set(true);
    const bulkRequest = {
      userIds,
      roles: roleNames,
    } as unknown as KeycloakBulkUserRolesRequest;
    this.usersService
      .unassignRolesBulk(bulkRequest, 'body', false)
      .pipe(
        switchMap((body: unknown) => {
          if (body instanceof Blob && body.type?.includes('json')) {
            return from(body.text()).pipe(map(() => undefined));
          }
          return of(undefined);
        }),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'configuration.user_roles_assignment.messages.unassign_success',
            ),
          });
          this.selectedUsers.set([]);
          this.searchTrigger$.next({});
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'configuration.user_roles_assignment.messages.unassign_failed',
            ),
          });
        },
      });
  }

  /** One API call per BU; unassigns the given users from each BU. */
  private executeUnassignBusinessUnitsBulk(
    businessUnits: { businessUnitId: number; userIds: string[] }[],
  ): void {
    if (businessUnits.length === 0) return;
    this.loading.set(true);
    const requests = businessUnits.map(({ businessUnitId, userIds }) =>
      this.userRolesAssignmentService.unassignUsersFromBusinessUnitBulk({
        businessUnitId,
        userIds: new Set(userIds),
      }),
    );
    forkJoin(requests)
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'configuration.user_roles_assignment.messages.unassign_bu_success',
            ),
          });
          this.selectedUsers.set([]);
          this.searchTrigger$.next({});
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'configuration.user_roles_assignment.messages.unassign_bu_failed',
            ),
          });
        },
      });
  }

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    const btn =
      (target.closest('p-button')?.querySelector('button') as
        | HTMLElement
        | undefined) ??
      (target.closest('button') as HTMLElement) ??
      target;
    if (btn && this.advancedTable) {
      this.advancedTable.openColumnSelector(btn);
    }
  }
}
